import React, { useState, useEffect, useMemo, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ISubnetResponse } from '../utils/types';
import { ChevronLeftIcon, ChevronRightIcon } from './SVGIcons';
import { formatNumber } from '../utils';
import { LoadingIcon } from './SVGIcons';

interface SubnetsTableProps {
    subnets: ISubnetResponse[];
    loading?: boolean;
}

const SubnetsTable: React.FC<SubnetsTableProps> = ({ subnets, loading = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<keyof ISubnetResponse>('emission');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const navigate = useNavigate();
    const { theme } = useTheme();

    const handleSort = (field: keyof ISubnetResponse) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const SortIcon: React.FC<{ field: keyof ISubnetResponse }> = ({ field }) => {
        if (sortField !== field) return null;
        return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
    };

    const formatPrice = (price: number): string => {
        return `τ${price.toFixed(4)}`;
    };

    const formatPercentage = (value: number): JSX.Element => {
        const color = value >= 0 ? 'text-green-600' : 'text-red-600';
        const sign = value >= 0 ? '+' : '';
        return <span className={color}>{sign}{value.toFixed(2)}%</span>;
    };

    const formatLast7Days = (trends: number[]): JSX.Element => {
        if (!trends || trends.length === 0) return <span className="text-gray-400">-</span>;

        const data = trends.slice(-7);
        const max = Math.max(...data);
        const min = Math.min(...data);

        // SVG dimensions
        const width = 100; // Use 100 for viewBox width
        const height = 24;

        // Map data to full width of SVG
        const points = data.map((v, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - ((v - min) / (max - min || 1)) * (height - 4); // 2px padding top/bottom
            return `${x},${y}`;
        }).join(' ');

        const lastValue = data[data.length - 1];
        const firstValue = data[0];
        const isUp = lastValue >= firstValue;
        const color = isUp ? '#00e6e6' : '#ff4d4f';

        return (
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <polyline
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    points={points}
                />
            </svg>
        );
    };

    const filteredAndSortedSubnets = useMemo(() => {
        let filtered = subnets;

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(subnet =>
                subnet.name.toLowerCase().includes(searchLower) ||
                subnet.symbol.toLowerCase().includes(searchLower) ||
                subnet.netuid.toString().includes(searchLower)
            );
        }

        const sorted = [...filtered].sort((a, b) => {
            const aValue = a[sortField];
            const bValue = b[sortField];

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return 0;
        });

        return sorted;
    }, [subnets, searchTerm, sortField, sortDirection]);

    const paginatedSubnets = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredAndSortedSubnets.slice(startIndex, endIndex);
    }, [filteredAndSortedSubnets, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredAndSortedSubnets.length / itemsPerPage);

    const thClassName = "px-4 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider font-borna cursor-pointer hover:bg-black/10 dark:hover:bg-white/10 transition-colors";

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin">
                    <LoadingIcon fillColor={theme === 'dark' ? '#fff' : '#000'} className='w-[40px] h-[40px]' />
                </div>
            </div>
        );
    }

    return (
        <div className="relative transition-colors duration-300">
            <div
                className="absolute inset-0 rounded-[24px] pointer-events-none"
                style={{
                    padding: '1px',
                    background: `linear-gradient(to bottom, ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)'}, ${theme === 'dark' ? 'rgba(255,255,255,0)' : 'rgba(0,0,0,0)'}`,
                    WebkitMask:
                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: 10,
                }}
            >
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 sm:px-6 py-4 space-y-4 sm:space-y-0">
                <h2 className="text-xl text-gray-800 dark:text-gray-200">
                    Subnets ({filteredAndSortedSubnets.length})
                </h2>

                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                    <div className="relative w-full sm:w-auto">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            placeholder="Search subnets..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pl-10 pr-4 py-0 w-full sm:w-auto"
                        />
                    </div>
                    <div className="relative w-full sm:w-auto">
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className={`input-field text-black dark:text-white bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pr-10 pl-4 py-0 appearance-none w-full sm:w-auto ${theme === 'dark' ? 'dark' : ''}`}
                        >
                            <option value="25">25 per page</option>
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                        </select>
                        <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </div>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className="block lg:hidden px-4 sm:px-6 pb-4">
                {paginatedSubnets.map((subnet) => (
                    <div
                        key={subnet.netuid}
                        onClick={() => navigate(`/subnet/${subnet.netuid}`)}
                        className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                        {subnet.name}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        #{subnet.netuid}
                                    </span>
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                        {subnet.symbol}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400">Price:</span>
                                        <span className="ml-1 text-gray-900 dark:text-gray-200">{formatPrice(subnet.price)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400">Market Cap:</span>
                                        <span className="ml-1 text-gray-900 dark:text-gray-200">τ{formatNumber(subnet.market_cap)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400">24h:</span>
                                        <span className="ml-1">{formatPercentage(subnet.price_change_24h)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400">Volume:</span>
                                        <span className="ml-1 text-gray-900 dark:text-gray-200">τ{formatNumber(subnet.volume_24h)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-[rgba(0,0,0,0.06)] dark:divide-white/20">
                        <thead className="">
                            <tr>
                                <th className={thClassName} onClick={() => handleSort('netuid')}>
                                    Subnet ID <SortIcon field="netuid" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('name')}>
                                    Title <SortIcon field="name" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('emission')}>
                                    Emission <SortIcon field="emission" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('price')}>
                                    Price <SortIcon field="price" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('price_change_1h')}>
                                    1h <SortIcon field="price_change_1h" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('price_change_24h')}>
                                    24h <SortIcon field="price_change_24h" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('price_change_1w')}>
                                    1w <SortIcon field="price_change_1w" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('market_cap')}>
                                    Market Cap <SortIcon field="market_cap" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('volume_24h')}>
                                    Volume 24h <SortIcon field="volume_24h" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('liquidity')}>
                                    Liquidity <SortIcon field="liquidity" />
                                </th>
                                <th className={thClassName} onClick={() => handleSort('symbol')}>
                                    Symbol <SortIcon field="symbol" />
                                </th>
                                <th className={thClassName}>
                                    Last 7 Days
                                </th>
                            </tr>
                        </thead>
                        <tbody className="">
                            {paginatedSubnets.map((subnet) => (
                                <tr
                                    key={subnet.netuid}
                                    onClick={() => navigate(`/subnet/${subnet.netuid}`)}
                                    className="cursor-pointer hover:bg-[#F2F2F2] dark:hover:bg-[#262626] transition-colors"
                                >
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                                        #{subnet.netuid}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                        {subnet.name}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                        {formatNumber(subnet.emission * 100)}%
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                                        {formatPrice(subnet.price)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {formatPercentage(subnet.price_change_1h)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {formatPercentage(subnet.price_change_24h)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {formatPercentage(subnet.price_change_1w)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                        τ{formatNumber(subnet.market_cap)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                        τ{formatNumber(subnet.volume_24h)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                        τ{formatNumber(subnet.liquidity)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-600 dark:text-gray-300">
                                        {subnet.symbol}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm min-w-[100px]">
                                        {formatLast7Days(subnet.last_7days_trends)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 px-4 sm:px-6 space-y-3 sm:space-y-0">
                <div className="text-sm text-black/50 dark:text-white/50">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedSubnets.length)} of {filteredAndSortedSubnets.length} subnets
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
                    >
                        <ChevronLeftIcon fillColor={currentPage === 1 ? 'gray' : theme === 'dark' ? 'white' : 'black'} />
                    </button>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
                    >
                        <ChevronRightIcon fillColor={currentPage === totalPages ? 'gray' : theme === 'dark' ? 'white' : 'black'} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubnetsTable; 